import { uniqBy, findIndex } from 'lodash-es';

import {
  getPanels,
  getMultiplePanels,
  duplicatePanel,
  getTags,
} from '../../../services/panels';
import { collectTimelineValues } from '../../../utils/timeline-collectors';

const state = {
  panels: [],
  interactiveTags: [],
  lookupTable: {},

  // Track which panels are being loaded by ID
  isLoadingMap: {},

  // Track which panels are confirmed missing (deleted) by ID
  notFoundMap: {},

  isLoading: false,
  page: 1,
  limit: 20,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  async getPanels(context) {
    const { page, limit, tags, search } = context.state;

    context.commit('SET_PANELS_FIELD', { key: 'isLoading', value: true });

    const { panels, total } = await getPanels({
      page,
      limit,
      tags: tags.join(','),
      search,
    });

    context.commit('SET_PANELS_FIELD', { key: 'total', value: total });
    context.commit('SET_PANELS', panels);
    context.commit('ADD_PANELS_TO_LOOKUP_TABLE', panels);
    context.commit('SET_PANELS_FIELD', { key: 'isLoading', value: false });
  },

  async getInteractiveTags({ commit, rootState }) {
    const { owner } = rootState.production.production;
    const { tags } = await getTags(owner);
    const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));

    commit('SET_PANELS_FIELD', { key: 'interactiveTags', value: sortedTags });
  },

  /**
   * Pull smart panels from current production's playlist, and fetch just those panels.
   */
  async getTimelinePanels(context) {
    const { production } = context.rootState.production;
    const { timeline } = production;

    const panelIds = [...collectTimelineValues(timeline, (item) => {
      const panel = context.state.lookupTable[item._id];

      // Ignore if we already have the panel loaded in the lookup table,
      // or it's already been confirmed missing.
      if (item.layout === 'panel' && !panel?.settings && !context.state.notFoundMap[item._id] && item._id) {
        return item._id;
      }

      return null;
    }, new Set())];

    if (!panelIds.length) {
      return;
    }

    context.commit('SET_PANELS_LOADING', { ids: panelIds, loading: true });

    try {
      const { panels } = await getMultiplePanels(panelIds);
      if (panels?.length) {
        context.commit('ADD_PANELS_TO_LOOKUP_TABLE', panels);
      }

      // Any panel requested but missing from the response is presumed deleted.
      const returnedIds = new Set((panels || []).map((panel) => panel._id));
      const notFoundIds = panelIds.filter((id) => !returnedIds.has(id));
      if (notFoundIds.length) {
        context.commit('SET_PANELS_NOT_FOUND', { ids: notFoundIds, notFound: true });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch multiple panels', err);
    } finally {
      context.commit('SET_PANELS_LOADING', { ids: panelIds, loading: false });
    }
  },

  changeInteractiveTags({ commit, dispatch }, tags) {
    commit('SET_PANELS_FIELD', { key: 'tags', value: tags });

    commit('RESET_PANELS');

    dispatch('getPanels');
  },

  changeInteractiveSearch({ commit, dispatch }, search) {
    commit('SET_PANELS_FIELD', { key: 'search', value: search });

    commit('RESET_PANELS');

    dispatch('getPanels');
  },

  resetInteractiveSearch(context) {
    context.commit('CLEAR_PANELS_TAGS');
    context.commit('SET_PANELS_FIELD', { key: 'search', value: '' });
    context.dispatch('resetPanels');
    context.dispatch('getPanels');
  },

  incrementPanelsPage(context) {
    context.commit('SET_PANELS_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getPanels');
  },

  resetPanels(context) {
    context.commit('RESET_PANELS');
  },

  async duplicatePanel(context, panelId) {
    const panel = await duplicatePanel(panelId);
    context.commit('DUPLICATE_PANEL', panel);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SET_PANELS(state, panels) {
    state.panels = uniqBy(state.panels.concat(panels), '_id');
  },

  RESET_PANELS(state) {
    state.panels = [];
    state.page = 1;
  },

  ADD_PANELS_TAG(state, tag) {
    state.tags = [...state.tags, tag];
  },

  REMOVE_PANELS_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_PANELS_TAGS(state) {
    state.tags = [];
  },

  /**
   * Merge provided panels into lookupTable.
   * Each panel will be merged with the existing one,
   * if it already exists in state, so we don't clear anything like `settings`.
   */
  ADD_PANELS_TO_LOOKUP_TABLE(state, panels) {
    // Yo dawg, I heard you like spread operators...
    state.lookupTable = {
      ...state.lookupTable,
      ...(panels.reduce((memo, panel) => ({
        ...memo,
        [panel._id]: {
          ...state.lookupTable[panel._id],
          ...panel,
        },
      }), {})),
    };
  },

  SET_PANELS_FIELD(state, { key, value }) {
    state[key] = value;
  },

  /**
   * Set loading flags for specific panels by ID,
   * so the playlist can show a skeleton while they load.
   */
  SET_PANELS_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_PANELS_NOT_FOUND(state, { ids, notFound }) {
    state.notFoundMap = {
      ...state.notFoundMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: notFound,
      }), {}),
    };
  },


  DUPLICATE_PANEL(state, payload) {
    state.panels.unshift(payload);
    state.lookupTable[payload._id] = payload;
  },

  EDIT_PANEL(state, payload) {
    const index = findIndex(state.panels, (o) => o._id === payload._id);
    state.panels.splice(index, 1, payload);
    state.lookupTable[payload._id] = payload;
  },
};

const getters = {
  hasMoreSmartPanels(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
